import { useDocumentClassLabelAi } from "../../provider/DocumentClassLabelAiProvider";
import React, { useEffect, useState } from "react";
import { AutoCompleteOptionWithValue } from "../../types/AutoCompleteOptionWithValue";
import { useActionIdentifier } from "../../provider/ActionIdentifierProvider";
import Grid from "@mui/material/Grid";
import { MediaObject } from "../../types/MediaObject";
import {
  AutocompleteElement,
  RadioButtonGroup,
  SelectElement,
  TextFieldElement,
  useFormContext,
} from "react-hook-form-mui";
import { requiredFieldDefaultFieldBased } from "../../services/reactHookFormValidationRules";
import { FormControl, FormLabel } from "@mui/material";

type MediaObjectLabelAndActionFormProps = {
  productClassName: string;
  mediaObject?: MediaObject;
  showExtraFields: boolean;
};

export default function MediaObjectLabelAndActionForm({
  productClassName,
  mediaObject,
  showExtraFields,
}: MediaObjectLabelAndActionFormProps) {
  const [documentClassLabelAiOptions, setDocumentClassLabelAiOptions] = useState<AutoCompleteOptionWithValue[]>([]);
  const [actionIdentifierOptions, setActionIdentifierOptions] = useState<AutoCompleteOptionWithValue[]>([]);
  const formContext = useFormContext();
  const {
    getDocumentClassLabelsAiByProductClass,
    prepareAndSortLabels,
    isLoading: isLoadingDocumentClassLabels,
  } = useDocumentClassLabelAi();

  const {
    getActionIdentifiersByLabel,
    prepareAndSortActionIdentifiers,
    isLoading: isLoadingActionIdentifiers,
  } = useActionIdentifier();

  useEffect(() => {
    const newDocumentClassLabelAiOptions = prepareAndSortLabels(
      getDocumentClassLabelsAiByProductClass(productClassName)
    );
    setDocumentClassLabelAiOptions(newDocumentClassLabelAiOptions);
    if (mediaObject?.documentClassLabelAi) {
      formContext.setValue(
        "documentClassLabelAi",
        newDocumentClassLabelAiOptions.find((option) => option.value === mediaObject.documentClassLabelAi) ?? null
      );
    }
    if (mediaObject?.actionIdentifier) {
      formContext.setValue(
        "actionIdentifier",
        prepareAndSortActionIdentifiers(getActionIdentifiersByLabel(mediaObject.documentClassLabelAi!)).find(
          (option) => option.value === mediaObject.actionIdentifier
        ) ?? null
      );
    }
  }, [isLoadingDocumentClassLabels, isLoadingActionIdentifiers]);

  useEffect(() => {
    if (!formContext.watch("documentClassLabelAi")) {
      return;
    }
    let labelSpecificIdentifiers = prepareAndSortActionIdentifiers(
      getActionIdentifiersByLabel(formContext.watch("documentClassLabelAi")?.value)
    );
    setActionIdentifierOptions(labelSpecificIdentifiers);
    if (labelSpecificIdentifiers.find((option) => option.value === formContext.watch("actionIdentifier")?.value)) {
      return;
    }

    if (formContext.watch("documentClassLabelAi").value === "NO_LABEL") {
      formContext.setValue(
        "actionIdentifier",
        labelSpecificIdentifiers.find((item) => item.value === "OPS_ACTIVITY") ?? null
      );
    } else {
      formContext.setValue(
        "actionIdentifier",
        labelSpecificIdentifiers.length === 1 ? labelSpecificIdentifiers[0] : null
      );
    }

  }, [formContext.watch("documentClassLabelAi"), isLoadingActionIdentifiers, mediaObject]);

  useEffect(() => {
    if (formContext.getValues("sendMessageToCustomer") === "true") {
      formContext.setValue("showInGallery", "true");
    }
  }, [formContext.watch("sendMessageToCustomer")]);

  return (
    <>
      <Grid size={6}>
        <AutocompleteElement
          name="documentClassLabelAi"
          options={documentClassLabelAiOptions}
          label={"Dokumentenart"}
          rules={{ ...requiredFieldDefaultFieldBased }}
          autocompleteProps={{
            isOptionEqualToValue: (option: AutoCompleteOptionWithValue, value: AutoCompleteOptionWithValue) =>
              option.value === value?.value,
          }}
        />
      </Grid>
      <Grid size={6}>
        <AutocompleteElement
          name="actionIdentifier"
          options={actionIdentifierOptions}
          label={"Aktionskenner"}
          autocompleteProps={{
            isOptionEqualToValue: (option: AutoCompleteOptionWithValue, value: AutoCompleteOptionWithValue) =>
              option.value === value?.value,
          }}
          rules={{ ...requiredFieldDefaultFieldBased }}
        />
      </Grid>
      {showExtraFields && (
        <>
          <Grid size={12}>
            <TextFieldElement name={"description"} label={"Beschreibungstext"} rules={{ ...requiredFieldDefaultFieldBased }} />
          </Grid>
          <Grid size={12}>
            <SelectElement
              name={"electronicFileFolderPath"}
              label={"Ablage in Ordner"}
              rules={{ ...requiredFieldDefaultFieldBased }}
              options={[
                { id: "Mandantschaft", label: "Mandantschaft" },
                { id: "Gerichtliches Verfahren", label: "Gerichtliches Verfahren" },
                { id: "Außergerichtliches Verfahren", label: "Außergerichtliches Verfahren" },
                { id: "RSV", label: "RSV" },
                { id: "ToDo", label: "ToDo" },
                { id: "Rechnungen", label: "Rechnungen" },
                { id: "Sonstiges", label: "Sonstiges" },
              ]}
            />
          </Grid>
          <Grid size={6}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Für Mandantschaft sichtbar</FormLabel>
              <RadioButtonGroup
                row={true}
                name={"showInGallery"}
                options={[
                  { id: "true", label: "Ja" },
                  { id: "false", label: "Nein", disabled: formContext.watch("sendMessageToCustomer") === "true" },
                ]}
              />
            </FormControl>
          </Grid>
          <Grid size={6}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Mandantschaft benachrichtigen</FormLabel>
              <RadioButtonGroup
                row={true}
                name={"sendMessageToCustomer"}
                options={[
                  { id: "true", label: "Ja" },
                  { id: "false", label: "Nein" },
                ]}
              />
            </FormControl>
          </Grid>
        </>
      )}
    </>
  );
}
